import React, { useState } from 'react';
import { Table, TableHead, TableBody, TableRow, MenuItem, InputAdornment, Box } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import SmsTemplateEdit from './SmsTemplateEdit';
import {
  ControlsRow,
  PageSizeGroup,
  PageSizeLabel,
  PageSizeSelect,
  SearchInput,
  CreateButton,
  TableWrapper,
  HeaderCell,
  BodyCell,
  DataRow,
  EditButton,
  PaginationContainer,
  PageButton,
  NavButton,
  EllipsisText,
} from './SmsTemplateManagementStyled';
import { useQuery } from '@tanstack/react-query';
import { getSmsTemplatesList } from '../../../api/sms';

const COLUMNS = [
  { id: 'name', label: '템플릿 명', width: '240px' },
  { id: 'content', label: '템플릿 내용' },
  { id: 'action', label: '기타', width: '188px' },
];

const truncate = (text, max) => {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max)}...` : text;
};

const SmsTemplateManagement = () => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state.user);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const branchId = user?.branch?.branchId;

  // 템플릿 목록 조회
  const { data: templateListData = [] } = useQuery({
    queryKey: ['smsTemplates', branchId],
    queryFn: () => getSmsTemplatesList(branchId, user?.token),
    enabled: !!branchId && !!user?.token,
  });

  const keyword = searchKeyword.trim().toLowerCase();
  const filteredList = keyword
    ? templateListData.filter(
        item =>
          item.templateName?.toLowerCase().includes(keyword) ||
          item.content?.toLowerCase().includes(keyword),
      )
    : templateListData;

  const totalPages = Math.max(1, Math.ceil(filteredList.length / pageSize));
  const list = filteredList.slice((page - 1) * pageSize, page * pageSize);

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      setSearchKeyword(searchValue);
      setPage(1);
    }
  };

  const handleEdit = template => {
    setEditingTemplate(template);
    setIsEditOpen(true);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setIsEditOpen(true);
  };

  const handleEditClose = () => {
    setIsEditOpen(false);
    setEditingTemplate(null);
  };

  const renderPagination = () => {
    const maxVisible = 3;
    let startPage = Math.max(1, page - 1);
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) pages.push(i);

    const showLastEllipsis = endPage < totalPages - 1;
    const showLastPage = endPage < totalPages;

    return (
      <PaginationContainer>
        <NavButton onClick={() => setPage(prev => Math.max(1, prev - 1))} disabled={page === 1}>
          <KeyboardArrowLeftIcon />
        </NavButton>
        {pages.map(p => (
          <PageButton key={p} isActive={p === page} onClick={() => setPage(p)}>
            {p}
          </PageButton>
        ))}
        {showLastEllipsis && <EllipsisText>...</EllipsisText>}
        {showLastPage && (
          <PageButton isActive={page === totalPages} onClick={() => setPage(totalPages)}>
            {totalPages}
          </PageButton>
        )}
        <NavButton onClick={() => setPage(prev => Math.min(totalPages, prev + 1))} disabled={page === totalPages}>
          <KeyboardArrowRightIcon />
        </NavButton>
      </PaginationContainer>
    );
  };

  return (
    <>
      <ControlsRow>
        <PageSizeGroup>
          <PageSizeLabel>{t('페이지당 항목 수')}</PageSizeLabel>
          <PageSizeSelect
            value={pageSize}
            onChange={e => {
              setPageSize(e.target.value);
              setPage(1);
            }}>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </PageSizeSelect>
        </PageSizeGroup>

        <SearchInput
          placeholder={t('검색어를 입력해 주세요')}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon style={{ color: '#a6a6a6' }} />
              </InputAdornment>
            ),
          }}
        />

        <CreateButton onClick={handleCreate}>
          <AddIcon />
          {t('템플릿 만들기')}
        </CreateButton>
      </ControlsRow>

      <TableWrapper>
        <Table>
          <TableHead>
            <TableRow>
              {COLUMNS.map(col => (
                <HeaderCell key={col.id} cellWidth={col.width}>
                  {t(col.label)}
                </HeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length > 0 ? (
              list.map(item => (
                <DataRow key={item.id} onClick={() => handleEdit(item)} style={{ cursor: 'pointer' }}>
                  <BodyCell cellWidth="240px">{truncate(item.templateName, 8)}</BodyCell>
                  <BodyCell>{truncate(item.content, 30)}</BodyCell>
                  <BodyCell cellWidth="188px">
                    <Box display="flex" justifyContent="center">
                      <EditButton
                        onClick={e => {
                          e.stopPropagation();
                          handleEdit(item);
                        }}>
                        {t('수정')}
                      </EditButton>
                    </Box>
                  </BodyCell>
                </DataRow>
              ))
            ) : (
              <DataRow>
                <BodyCell colSpan={COLUMNS.length} style={{ textAlign: 'center', color: '#a6a6a6' }}>
                  {t('데이터가 없습니다')}
                </BodyCell>
              </DataRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {renderPagination()}

      <SmsTemplateEdit open={isEditOpen} onClose={handleEditClose} templateData={editingTemplate} />
    </>
  );
};

export default SmsTemplateManagement;
